import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicPostsAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import Card from '../common/Card';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
import Loading from '../common/Loading';
import ImageViewer from './ImageViewer';
import PlatformPreview from './previews';
import { formatDate, handleApiError, getImageUrl, showToast, showConfirm, getMediaTypeFromUrl } from '../../utils/helpers';
import { PLATFORMS, CONTENT_TYPES } from '../../utils/platformConfig';

const PublicPostReviewPage = () => {
  const { token } = useParams();
  const { isRTL, isArabic } = useLanguage();
  const [showPreview, setShowPreview] = useState(false);
  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);
  const [changeRequestText, setChangeRequestText] = useState('');

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchPostData();
  }, [token]);

  const fetchPostData = async () => {
    try {
      setLoading(true);
      setError(null);

      const postResponse = await publicPostsAPI.getByToken(token);

      // Check if response has data
      if (!postResponse || !postResponse.data) {
        throw new Error('Invalid response from server');
      }

      const postData = postResponse.data.post;
      const commentsData = postResponse.data.comments || [];
      
      // Check if post data exists
      if (!postData) {
        throw new Error('Post not found');
      }
      
      // Parse JSONB fields if needed
      if (postData.specifications && typeof postData.specifications === 'string') {
        try {
          postData.specifications = JSON.parse(postData.specifications);
        } catch (e) {
          console.warn('Failed to parse specifications:', e);
          postData.specifications = {};
        }
      }
      
      if (postData.current_carousel_items) {
        if (typeof postData.current_carousel_items === 'string') {
          try {
            postData.current_carousel_items = JSON.parse(postData.current_carousel_items);
          } catch (e) {
            console.warn('Failed to parse carousel items:', e);
            postData.current_carousel_items = [];
          }
        }
      } else {
        postData.current_carousel_items = [];
      }

      // Parse social_media_accounts if needed
      if (postData.client_social_media_accounts && typeof postData.client_social_media_accounts === 'string') {
        try {
          postData.client_social_media_accounts = JSON.parse(postData.client_social_media_accounts);
        } catch (e) {
          console.warn('Failed to parse social_media_accounts:', e);
          postData.client_social_media_accounts = {};
        }
      }

      setPost(postData);
      setComments(commentsData);
    } catch (err) {
      console.error('Error fetching post data:', err);
      const errorMessage = err.response?.data?.error || err.message || (isRTL ? 'فشل تحميل البوست' : 'Failed to fetch post');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    const confirmed = await showConfirm(
      isRTL 
        ? 'هل أنت متأكد من الموافقة على هذا البوست؟' 
        : 'Are you sure you want to approve this post?',
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(true);
      await publicPostsAPI.approveByToken(token);
      await fetchPostData();
      showToast.success(isRTL ? 'تمت الموافقة بنجاح!' : 'Post approved successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestChanges = async () => {
    setShowChangeRequestModal(true);
  };

  const handleSubmitChangeRequest = async () => {
    if (!changeRequestText.trim()) {
      showToast.error(isRTL ? 'يرجى كتابة التعديلات المطلوبة' : 'Please enter the changes needed');
      return;
    }

    try {
      setActionLoading(true);
      await publicPostsAPI.rejectByToken(token, { comment: changeRequestText.trim() });
      await fetchPostData();
      setShowChangeRequestModal(false);
      setChangeRequestText('');
      showToast.success(
        isRTL 
          ? 'تم طلب التعديلات بنجاح!' 
          : 'Changes requested successfully!'
      );
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelChangeRequest = () => {
    setShowChangeRequestModal(false);
    setChangeRequestText('');
  };

  const handleAddComment = async (data) => {
    try {
      await publicPostsAPI.addCommentByToken(token, data);
      await fetchPostData();
      showToast.success(isRTL ? 'تمت إضافة التعليق بنجاح!' : 'Comment added successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {isRTL ? 'خطأ في تحميل البوست' : 'Error loading post'}
          </h1>
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchPostData} variant="primary">
            {isRTL ? 'إعادة المحاولة' : 'Try Again'}
          </Button>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {isRTL ? 'البوست غير موجود' : 'Post not found'}
          </h1>
          <p className="text-gray-600 mb-4">
            {isRTL 
              ? 'الرابط غير صحيح أو انتهت صلاحيته.' 
              : 'The link is invalid or has expired.'}
          </p>
        </div>
      </div>
    );
  }

  // Get platform and content type info
  const platformInfo = post.platform ? PLATFORMS[post.platform] : null;
  const contentTypeInfo = post.content_type && platformInfo?.contentTypes 
    ? platformInfo.contentTypes[post.content_type] 
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-4 sm:py-8 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="space-y-4">
            <div className="flex-1">
              {post.client_logo_url && (
                <img 
                  src={getImageUrl(post.client_logo_url)} 
                  alt={post.client_name || 'Client'} 
                  className="h-10 sm:h-12 mb-3 sm:mb-4"
                />
              )}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {post.title || (isRTL ? 'بوست بدون عنوان' : 'Untitled Post')}
                </h1>
                {post.status && <StatusBadge status={post.status} />}
              </div>
              {post.description && (
                <p className="text-gray-600 mt-2 text-sm sm:text-base">{post.description}</p>
              )}
              {post.client_name && (
                <p className="text-xs sm:text-sm text-gray-500 mt-2">
                  {isRTL ? 'العميل:' : 'Client:'} {post.client_name}
                </p>
              )}
            </div>

            {/* Action Buttons - Only show for pending posts - Mobile optimized */}
            {post.status === 'pending' && (
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="success"
                  onClick={handleApprove}
                  loading={actionLoading}
                  disabled={actionLoading}
                  className="w-full sm:w-auto sm:flex-1 text-base sm:text-sm py-3 sm:py-2"
                >
                  {isRTL ? '✓ تعميد التصميم' : '✓ Approve Design'}
                </Button>
                <Button
                  variant="danger"
                  onClick={handleRequestChanges}
                  disabled={actionLoading}
                  loading={actionLoading}
                  className="w-full sm:w-auto sm:flex-1 text-base sm:text-sm py-3 sm:py-2"
                >
                  {isRTL ? 'طلب تعديلات' : 'Request Changes'}
                </Button>
              </div>
            )}

            {/* Show message if already approved or needs changes */}
            {post.status === 'approved' && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                <p className="text-green-800 font-medium">
                  {isRTL ? '✓ تمت الموافقة على هذا البوست' : '✓ This post has been approved'}
                </p>
              </div>
            )}
            {post.status === 'needs_changes' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2">
                <p className="text-yellow-800 font-medium">
                  {isRTL ? '⚠ تم طلب تعديلات على هذا البوست' : '⚠ Changes have been requested for this post'}
                </p>
              </div>
            )}
          </div>

          {/* Mobile Preview - Show inline on mobile after buttons */}
          {post.current_image_url && (
            <div className="block sm:hidden mt-4 pt-4 border-t border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {isRTL ? 'معاينة' : 'Preview'}
              </h2>
              <div className="flex justify-center">
                <PlatformPreview
                  platform={post.platform}
                  contentType={post.content_type}
                  imageUrl={post.current_image_url ? getImageUrl(post.current_image_url) : null}
                  caption={post.current_caption}
                  title={post.title}
                  description={post.description}
                  richDescription={post.rich_description}
                  productDetails={post.product_details}
                  articleDetails={post.article_details}
                  specifications={post.specifications}
                  carouselItems={post.current_carousel_items || []}
                  username={
                    post.platform === 'instagram' && post.client_social_media_accounts?.instagram
                      ? post.client_social_media_accounts.instagram.replace('@', '')
                      : 'username'
                  }
                />
              </div>
            </div>
          )}

          {/* Post Meta Info - Hidden on mobile to keep it simple */}
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 hidden sm:block">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-600">{isRTL ? 'تاريخ الإنشاء' : 'Created'}</p>
                <p className="font-medium text-gray-900">{formatDate(post.created_at)}</p>
              </div>
              {post.designer_name && (
                <div>
                  <p className="text-gray-600">{isRTL ? 'المصمم' : 'Designer'}</p>
                  <p className="font-medium text-gray-900">{post.designer_name}</p>
                </div>
              )}
              {post.writer_name && (
                <div>
                  <p className="text-gray-600">{isRTL ? 'الكاتب' : 'Writer'}</p>
                  <p className="font-medium text-gray-900">{post.writer_name}</p>
                </div>
              )}
              <div>
                <p className="text-gray-600">{isRTL ? 'النسخة' : 'Version'}</p>
                <p className="font-medium text-gray-900">v{post.current_version || 1}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Platform Info - Hidden on mobile to keep it simple */}
        {platformInfo && (
          <Card className="p-4 hidden sm:block">
            <div className="flex items-center gap-3">
              <div 
                className="flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium text-white"
                style={{ backgroundColor: platformInfo.color }}
              >
                <span>{platformInfo.icon}</span>
                <span>{isArabic ? platformInfo.nameAr : platformInfo.name}</span>
              </div>
              {contentTypeInfo && (
                <span className="text-sm text-gray-600">
                  {isArabic ? contentTypeInfo.nameAr : contentTypeInfo.name}
                </span>
              )}
              {post.status === 'pending' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowPreview(true)}
                  className={isRTL ? 'mr-auto' : 'ml-auto'}
                >
                  {isRTL ? 'معاينة' : 'Preview'}
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Image Display */}
        <Card className="p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
            {isRTL ? 'التصميم' : 'Design'}
          </h2>
          {post.current_image_url ? (
            <div>
              <ImageViewer
                imageUrl={post.current_image_url ? getImageUrl(post.current_image_url) : null}
                mediaType={post.current_image_url ? getMediaTypeFromUrl(post.current_image_url) : null}
                carouselItems={post.current_carousel_items || []}
                comments={comments || []}
                onAddComment={handleAddComment}
                canComment={post.status === 'pending'}
              />
            </div>
          ) : (
            <div className="aspect-video bg-gray-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-gray-300">
              <p className="text-gray-500">
                {isRTL ? 'لا توجد صورة مرفوعة بعد' : 'No image uploaded yet'}
              </p>
            </div>
          )}
        </Card>

        {/* Caption */}
        {post.current_caption && (
          <Card className="p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
              {isRTL ? 'الوصف' : 'Caption'}
            </h2>
            <div className="prose max-w-none">
              <p className="text-sm sm:text-base text-gray-700 whitespace-pre-wrap">{post.current_caption}</p>
            </div>
          </Card>
        )}

        {/* Article Content - rich_description for blog posts */}
        {post.content_type === 'blog' && post.rich_description && (
          <Card className="p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
              {isRTL ? 'محتوى المقالة' : 'Article Content'}
            </h2>
            <div 
              className="text-gray-700 leading-relaxed text-sm sm:text-base [&>p]:mb-3 [&>p:last-child]:mb-0 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mb-2 [&>h1]:mt-4 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mb-2 [&>h2]:mt-3 [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:mb-2 [&>h3]:mt-2 [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-3 [&>ol]:list-decimal [&>ol]:ml-6 [&>ol]:mb-3 [&>li]:mb-1 [&>a]:text-primary-600 [&>a]:hover:text-primary-700 [&>a]:underline [&>strong]:font-bold [&>em]:italic [&>img]:rounded-lg [&>img]:my-4 [&>img]:max-w-full [&>img]:h-auto"
              dangerouslySetInnerHTML={{ __html: post.rich_description }}
            />
          </Card>
        )}

        {/* Product Description - rich_description for product posts */}
        {post.content_type === 'product' && post.rich_description && (
          <Card className="p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
              {isRTL ? 'وصف المنتج' : 'Product Description'}
            </h2>
            <div 
              className="text-gray-700 leading-relaxed text-sm sm:text-base [&>p]:mb-3 [&>p:last-child]:mb-0 [&>h1]:text-xl [&>h1]:font-bold [&>h1]:mb-2 [&>h1]:mt-3 [&>h2]:text-lg [&>h2]:font-bold [&>h2]:mb-2 [&>h2]:mt-2 [&>h3]:text-base [&>h3]:font-semibold [&>h3]:mb-2 [&>h3]:mt-2 [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-3 [&>ol]:list-decimal [&>ol]:ml-6 [&>ol]:mb-3 [&>li]:mb-1 [&>a]:text-primary-600 [&>a]:hover:text-primary-700 [&>a]:underline [&>strong]:font-bold [&>em]:italic [&>img]:rounded-lg [&>img]:my-4 [&>img]:max-w-full [&>img]:h-auto"
              dangerouslySetInnerHTML={{ __html: post.rich_description }}
            />
          </Card>
        )}

        {/* Change Request Modal */}
        {showChangeRequestModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  {isRTL ? 'طلب تعديلات' : 'Request Changes'}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">
                  {isRTL 
                    ? 'يرجى توضيح التعديلات المطلوبة على التصميم:' 
                    : 'Please explain what changes are needed on the design:'}
                </p>
                <textarea
                  value={changeRequestText}
                  onChange={(e) => setChangeRequestText(e.target.value)}
                  placeholder={isRTL 
                    ? 'مثال: يرجى تغيير اللون إلى أزرق، أو جعل الخط أكبر...' 
                    : 'Example: Please change the color to blue, or make the font larger...'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm sm:text-base"
                  rows="6"
                  autoFocus
                />
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <Button
                    variant="primary"
                    onClick={handleSubmitChangeRequest}
                    loading={actionLoading}
                    disabled={actionLoading || !changeRequestText.trim()}
                    className="w-full sm:flex-1 py-3 sm:py-2 text-base sm:text-sm"
                  >
                    {isRTL ? 'إرسال' : 'Submit'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancelChangeRequest}
                    disabled={actionLoading}
                    className="w-full sm:flex-1 py-3 sm:py-2 text-base sm:text-sm"
                  >
                    {isRTL ? 'إلغاء' : 'Cancel'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Platform Preview Modal */}
        {showPreview && post.current_image_url && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    {isRTL ? 'معاينة' : 'Preview'}
                  </h2>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  >
                    ×
                  </button>
                </div>
                <div className="flex justify-center">
                  <PlatformPreview
                    platform={post.platform}
                    contentType={post.content_type}
                    imageUrl={post.current_image_url ? getImageUrl(post.current_image_url) : null}
                    caption={post.current_caption}
                    title={post.title}
                    description={post.description}
                    richDescription={post.rich_description}
                    productDetails={post.product_details}
                    articleDetails={post.article_details}
                    specifications={post.specifications}
                    carouselItems={post.current_carousel_items || []}
                    username={
                      post.platform === 'instagram' && post.client_social_media_accounts?.instagram
                        ? post.client_social_media_accounts.instagram.replace('@', '')
                        : 'username'
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicPostReviewPage;

